import { Role } from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, toast } from '@utima/ui';

import { Card } from '@/components/card/Card';
import { DisplayValue } from '@/components/displayValue/DisplayValue';
import { Protected } from '@/components/protected/Protected';
import { invalidateCurrentOrganization } from '@/services/invalidateCurrentOrganization';
import { trpc } from '@/services/trpc';

export interface ResetMonthlyUsageProps {
  organizationId?: string;
}

export function ResetMonthlyUsage({ organizationId }: ResetMonthlyUsageProps) {
  const { t } = useLingui();
  const utils = trpc.useUtils();

  const resetMonthlyUsage = trpc.organizations.resetMonthlyUsage.useMutation({
    onSuccess: (data, { id }) => {
      toast.success(t`Monthly usage reset successfully`);
      utils.organizations.findOne.invalidate({ id });
      void invalidateCurrentOrganization(id);
    },
    onError: error => {
      toast.error(t`Failed to reset monthly usage: ${error.message}`);
      console.error(error);
    },
  });

  const resetBalance = trpc.organizations.resetBalance.useMutation({
    onSuccess: (data, { id }) => {
      toast.success(t`Balance reset successfully`);
      utils.organizations.findOne.invalidate({ id });
      void invalidateCurrentOrganization(id);
    },
    onError: error => {
      toast.error(t`Failed to reset balance: ${error.message}`);
      console.error(error);
    },
  });

  if (!organizationId) {
    return null;
  }

  return (
    <Protected roles={[Role.SuperAdmin]}>
      <Card title={t`Reset Usage & Balance`}>
        <DisplayValue label={t`Reset Monthly Usage`}>
          <div className='flex flex-col gap-2'>
            <p className='text-sm text-muted-fg'>
              <Trans>
                This will reset the monthly usage for the organization to 0 for
                current month. Usefull if anything happened with the monthly
                renewal.
              </Trans>
            </p>
            <Button
              variant='danger'
              disabled={resetMonthlyUsage.isPending}
              loading={resetMonthlyUsage.isPending}
              onClick={() => resetMonthlyUsage.mutate({ id: organizationId })}
            >
              <Trans>Reset Monthly Usage</Trans>
            </Button>
          </div>
        </DisplayValue>
        <DisplayValue label={t`Reset Balance`}>
          <div className='flex flex-col gap-2'>
            <p className='text-sm text-muted-fg'>
              <Trans>
                This will reset the balance to 0. BE CAREFUL, this will remove
                all the credit from the organization.
              </Trans>
            </p>
            <Button
              variant='danger'
              disabled={resetBalance.isPending}
              loading={resetBalance.isPending}
              onClick={() => resetBalance.mutate({ id: organizationId })}
            >
              <Trans>Reset Balance</Trans>
            </Button>
          </div>
        </DisplayValue>
      </Card>
    </Protected>
  );
}
